import React, { useCallback, useEffect, useState } from 'react';
import { FileText, Printer, Loader2, Building, School as SchoolIcon, Boxes, MapPin, ShieldAlert } from 'lucide-react';
import Sidebar from '../components/Sidebar';
import { getSupplyExecutiveDashboard } from '../api';

const numFmt = (n) => (n == null ? 'غير متوفر' : Number(n).toLocaleString('en-US'));
const moneyFmt = (n) => (n == null ? 'غير متوفر' : Number(n).toLocaleString('en-US', { maximumFractionDigits: 0 }));
const pctFmt = (n) => (n == null ? 'غير متوفر' : `${Number(n).toFixed(1)}%`);

const TABS = [
  { key: 'executive',  label: 'التقرير التنفيذي للتوريدات', icon: FileText },
  { key: 'suppliers',  label: 'تقرير الموردين',              icon: Building },
  { key: 'schools',    label: 'تقرير المدارس والتوزيع',       icon: SchoolIcon },
  { key: 'items',      label: 'تقرير الأصناف والكميات',       icon: Boxes },
  { key: 'geo',        label: 'التقرير الجغرافي',              icon: MapPin },
  { key: 'quality',    label: 'تقرير جودة البيانات',           icon: ShieldAlert },
];

export default function ReportsPage() {
  const [tab, setTab] = useState('executive');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try { setData(await getSupplyExecutiveDashboard()); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <div dir="rtl" className="supply-theme min-h-screen flex">
        <Sidebar />
        <main className="flex-1 flex items-center justify-center"><Loader2 size={26} className="animate-spin" style={{ color: '#9D8CFF' }} /></main>
      </div>
    );
  }

  return (
    <div dir="rtl" className="supply-theme min-h-screen flex">
      <Sidebar />
      <main className="flex-1 min-w-0 p-6 md:p-8" data-testid="reports-page">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
          <div>
            <div className="text-[11px] font-semibold tracking-widest" style={{ color: 'var(--se-violet-2)' }}>ANALYTICS & REPORTS</div>
            <h1 className="se-title mt-1">التحليلات والتقارير</h1>
            <div className="se-subtitle">تقارير التوريد فقط · بيانات حقيقية من الملفات المستوردة</div>
          </div>
          <button onClick={() => window.print()} className="se-btn se-btn-primary">
            <Printer size={13} /> طباعة / PDF
          </button>
        </div>

        {/* Tabs */}
        <div className="se-panel p-1 mb-4 inline-flex flex-wrap gap-1" data-testid="reports-tabs">
          {TABS.map((t) => (
            <button key={t.key} onClick={() => setTab(t.key)}
              className={`px-3 py-2 rounded-md text-[12px] font-semibold flex items-center gap-1.5 transition`}
              data-testid={`tab-${t.key}`}
              style={{
                background: tab === t.key ? 'linear-gradient(135deg, rgba(109,93,251,0.24), rgba(157,140,255,0.12))' : 'transparent',
                color: tab === t.key ? '#B7ADFF' : '#C7C3DB',
                border: tab === t.key ? '1px solid rgba(109,93,251,0.32)' : '1px solid transparent',
              }}>
              <t.icon size={13} /> {t.label}
            </button>
          ))}
        </div>

        {/* Content */}
        {tab === 'executive' && <ExecutiveReport data={data} />}
        {tab === 'suppliers' && <SuppliersReport data={data} />}
        {tab === 'schools'   && <SchoolsReport />}
        {tab === 'items'     && <ItemsReport />}
        {tab === 'geo'       && <GeoReport data={data} />}
        {tab === 'quality'   && <QualityReport data={data} />}
      </main>
    </div>
  );
}

function ExecutiveReport({ data }) {
  const k = data.kpis;
  return (
    <div className="space-y-4" data-testid="report-executive">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Row label="إجمالي الموردين" value={numFmt(k.totalSuppliers)} />
        <Row label="إجمالي المدارس" value={numFmt(k.totalSchools)} />
        <Row label="إجمالي الأصناف" value={numFmt(k.totalItems)} />
        <Row label="إجمالي الكميات المخصصة" value={numFmt(k.allocatedQuantity)} />
        <Row label="إجمالي القيمة" value={moneyFmt(k.allocatedValue)} unit="ريال" />
        <Row label="المدارس المشمولة" value={numFmt(k.coveredSchools)} />
        <Row label="عدد إدارات التعليم" value={numFmt(k.totalDepartments)} />
        <Row label="عدد المدن" value={numFmt(k.totalCities)} />
      </div>
      {data.hasDeliveryData && (
        <div className="se-panel p-4">
          <div className="se-section-head mb-3">تقدم التوريد الفعلي</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Row label="الكمية الموردة" value={numFmt(k.deliveredQuantity)} />
            <Row label="الكمية المتبقية" value={numFmt(k.remainingQuantity)} />
            <Row label="نسبة الإنجاز" value={pctFmt(k.completionRate)} />
            <Row label="الإنجاز المالي" value={pctFmt(k.financialCompletion)} />
          </div>
        </div>
      )}
      {(data.insights || []).length > 0 && (
        <div className="se-panel p-4">
          <div className="se-section-head mb-3">ذكاء التوريد</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {data.insights.map((i, ix) => (
              <div key={ix} className="text-[12px] p-2.5 rounded-md" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
                <div className="text-slate-400 text-[10.5px]">{i.label}</div>
                <div className="font-bold mt-0.5" style={{ color: i.warning ? '#FCD34D' : '#F1EEFB' }}>{i.value}</div>
                <div className="text-slate-500 text-[10.5px] mt-0.5">{i.detail}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function SuppliersReport({ data }) {
  return (
    <div className="se-panel overflow-x-auto" data-testid="report-suppliers">
      <table className="se-table">
        <thead><tr><th>المورد</th><th className="text-center">المدارس</th><th className="text-center">الأصناف</th><th className="text-center">الكمية المخصصة</th><th className="text-center">القيمة</th><th className="text-center">الإدارات</th><th className="text-center">المدن</th></tr></thead>
        <tbody>
          {(data.supplierPerformance || []).map((s, i) => (
            <tr key={i}>
              <td className="font-semibold">{s.name}</td>
              <td className="text-center tabular-nums">{numFmt(s.schools)}</td>
              <td className="text-center tabular-nums">{numFmt(s.items)}</td>
              <td className="text-center tabular-nums">{numFmt(s.allocated_qty)}</td>
              <td className="text-center tabular-nums">{moneyFmt(s.allocated_value)}</td>
              <td className="text-center tabular-nums">{numFmt(s.depts)}</td>
              <td className="text-center tabular-nums">{numFmt(s.cities)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function SchoolsReport() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/supply/schools`)
      .then((r) => r.json()).then((j) => { setRows(j.schools || []); setLoading(false); });
  }, []);
  if (loading) return <div className="se-panel p-8 text-center"><Loader2 size={22} className="animate-spin mx-auto" style={{ color: '#9D8CFF' }} /></div>;
  return (
    <div className="se-panel overflow-x-auto" data-testid="report-schools">
      <table className="se-table">
        <thead><tr><th>المدرسة</th><th>الرقم الوزاري</th><th>المورد</th><th>الإدارة</th><th>المدينة</th><th>المرحلة</th><th className="text-center">الأصناف</th><th className="text-center">الكمية</th><th className="text-center">القيمة</th></tr></thead>
        <tbody>
          {rows.slice(0, 500).map((r, i) => (
            <tr key={i}>
              <td className="font-semibold">{r.school_name || 'غير متوفر'}</td>
              <td className="tabular-nums text-slate-400">{r.school_id || '—'}</td>
              <td className="text-slate-400">{r.supplier || '—'}</td>
              <td className="text-slate-400">{r.governorate || '—'}</td>
              <td className="text-slate-400">{r.region || '—'}</td>
              <td className="text-slate-400">{r.stage || '—'}</td>
              <td className="text-center tabular-nums">{numFmt(r.items_count)}</td>
              <td className="text-center tabular-nums">{numFmt(r.allocated_qty)}</td>
              <td className="text-center tabular-nums">{moneyFmt(r.allocated_value)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {rows.length > 500 && <div className="text-center text-slate-500 text-[11px] py-2">عرض 500 من {numFmt(rows.length)}</div>}
    </div>
  );
}

function ItemsReport() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/supply/items`)
      .then((r) => r.json()).then((j) => { setItems(j.items || []); setLoading(false); });
  }, []);
  if (loading) return <div className="se-panel p-8 text-center"><Loader2 size={22} className="animate-spin mx-auto" style={{ color: '#9D8CFF' }} /></div>;
  return (
    <div className="se-panel overflow-x-auto" data-testid="report-items">
      <table className="se-table">
        <thead><tr><th className="w-8">#</th><th>الصنف</th><th className="text-center">الكمية</th><th className="text-center">القيمة</th><th className="text-center">الموردون</th><th className="text-center">المدارس</th></tr></thead>
        <tbody>
          {items.slice(0, 200).map((it, i) => (
            <tr key={i}>
              <td className="text-slate-500 tabular-nums">{i + 1}</td>
              <td className="font-semibold">{it.name}</td>
              <td className="text-center tabular-nums">{numFmt(it.qty)}</td>
              <td className="text-center tabular-nums">{moneyFmt(it.value)}</td>
              <td className="text-center tabular-nums">{numFmt(it.suppliers)}</td>
              <td className="text-center tabular-nums">{numFmt(it.schools)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function GeoReport({ data }) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="report-geo">
      <div className="se-panel overflow-x-auto">
        <div className="p-3 text-[13px] font-bold" style={{ color: '#F1EEFB' }}>التوريدات حسب إدارة التعليم</div>
        <table className="se-table">
          <thead><tr><th>الإدارة</th><th className="text-center">الكمية المخصصة</th><th className="text-center">المدارس</th><th className="text-center">الأصناف</th></tr></thead>
          <tbody>
            {(data.byDepartment || []).map((d, i) => (
              <tr key={i}>
                <td className="font-semibold">{d.name}</td>
                <td className="text-center tabular-nums">{numFmt(d.allocated_qty)}</td>
                <td className="text-center tabular-nums">{numFmt(d.schools)}</td>
                <td className="text-center tabular-nums">{numFmt(d.items)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="se-panel overflow-x-auto">
        <div className="p-3 text-[13px] font-bold" style={{ color: '#F1EEFB' }}>الأصناف الأعلى تخصيصاً</div>
        <table className="se-table">
          <thead><tr><th className="w-8">#</th><th>الصنف</th><th className="text-center">الكمية</th></tr></thead>
          <tbody>
            {(data.topItems || []).slice(0, 15).map((it, i) => (
              <tr key={i}>
                <td className="text-slate-500 tabular-nums">{i + 1}</td>
                <td>{it.name}</td>
                <td className="text-center tabular-nums">{numFmt(it.qty)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function QualityReport({ data }) {
  const dq = data.dataQuality || {};
  return (
    <div className="se-panel p-5" data-testid="report-quality">
      <div className="flex items-baseline justify-between mb-4">
        <div className="text-[14px] font-bold" style={{ color: '#F1EEFB' }}>مؤشر الجودة الشامل</div>
        <div className="text-[28px] font-extrabold tabular-nums" style={{ color: dq.health_score >= 90 ? '#34D399' : dq.health_score >= 70 ? '#FBBF24' : '#F87171' }}>
          {pctFmt(dq.health_score)}
        </div>
      </div>
      <div className="se-progress mb-4"><div style={{ width: `${dq.health_score || 0}%` }} /></div>
      <div className="grid grid-cols-2 gap-3">
        <Row label="إجمالي السجلات" value={numFmt(dq.total_records)} />
        <Row label="المدارس بإحداثيات صالحة" value={numFmt(dq.schools_with_coords)} />
        <Row label="المدارس بدون إحداثيات" value={numFmt(dq.schools_missing_coords)} warn={dq.schools_missing_coords > 0} />
        <Row label="سجلات بدون سعر وحدة" value={numFmt(dq.missing_unit_price)} warn={dq.missing_unit_price > 0} />
        <Row label="سجلات بدون كمية" value={numFmt(dq.missing_quantity)} warn={dq.missing_quantity > 0} />
        <Row label="سجلات بدون رقم مدرسة" value={numFmt(dq.missing_school_id)} warn={dq.missing_school_id > 0} />
      </div>
    </div>
  );
}

function Row({ label, value, unit, warn }) {
  return (
    <div className="se-panel p-3">
      <div className="text-[10.5px] text-slate-400">{label}</div>
      <div className="text-[18px] font-extrabold tabular-nums mt-0.5" style={{ color: warn ? '#FCD34D' : '#F1EEFB' }}>{value}</div>
      {unit && <div className="text-[10px] text-slate-500 mt-0.5">{unit}</div>}
    </div>
  );
}
